import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Button, TextField, List, ListItem, ListItemText, IconButton, Paper, Divider, Avatar, Badge, CircularProgress, Alert, Chip } from '@mui/material';
import { 
  ThumbUp, 
  Delete, 
  Lightbulb, 
  Chat,
  Add,
  Send,
  TrendingUp
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { collection, addDoc, deleteDoc, onSnapshot, doc, updateDoc, getDoc } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { AuthContext } from '../contexts/AuthContext';

interface HMW {
  id: string;
  text: string;
  votes: number;
  voters: string[];
  userName?: string;
}

const mockUserId = 'user1'; // Replace with real user ID from auth later

const HowMightWePhase: React.FC = () => {
  const { teamId, sprintId } = useParams<{ teamId: string; sprintId: string }>();
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;

  // HMWs
  const [hmws, setHmws] = useState<HMW[]>([]);
  const [newHmw, setNewHmw] = useState('');
  const [hmwLoading, setHmwLoading] = useState(false);
  const [hmwError, setHmwError] = useState<string | null>(null);

  // Comments
  const [comments, setComments] = useState<any[]>([]); // Changed to any[] to accommodate userId and userName
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  // Firestore listeners
  useEffect(() => {
    if (!teamId || !sprintId) return;
    // HMWs
    const unsubHmw = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/hmwBoard_hmws`), snap => {
      setHmws(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as HMW)));
    });
    // Comments
    const unsubComments = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/hmwBoard_comments`), snap => {
      setComments(snap.docs.map(doc => doc.data() as any));
    });
    return () => {
      unsubHmw();
      unsubComments();
    };
  }, [teamId, sprintId]);

  // Add HMW
  const handleAddHmw = async () => {
    if (newHmw.trim() && teamId && sprintId) {
      setHmwLoading(true);
      setHmwError(null);
      try {
        await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/hmwBoard_hmws`), {
          text: newHmw.trim(),
          votes: 0,
          voters: [],
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        });
        setNewHmw('');
      } catch (err) {
        setHmwError('Failed to add HMW.');
      }
      setHmwLoading(false);
    }
  };

  // Vote HMW
  const handleVote = async (id: string) => {
    if (!teamId || !sprintId) return;
    setHmwLoading(true);
    setHmwError(null);
    try {
      const hmwRef = doc(firestore, `teams/${teamId}/designSprints/${sprintId}/hmwBoard_hmws`, id);
      const hmwSnap = await getDoc(hmwRef);
      if (hmwSnap.exists()) {
        const data = hmwSnap.data();
        if (!data.voters.includes(mockUserId)) {
          await updateDoc(hmwRef, {
            votes: (data.votes || 0) + 1,
            voters: [...(data.voters || []), mockUserId]
          });
        }
      }
    } catch (err) {
      setHmwError('Failed to vote.');
    }
    setHmwLoading(false);
  };

  // Delete HMW
  const handleDeleteHmw = async (id: string) => {
    if (!teamId || !sprintId) return;
    try {
      await deleteDoc(doc(firestore, `teams/${teamId}/designSprints/${sprintId}/hmwBoard_hmws`, id));
    } catch (err) {
      setHmwError('Failed to delete HMW.');
    }
  };

  // Comments
  const handleAddComment = async () => {
    if (newComment.trim() && teamId && sprintId) {
      setCommentLoading(true);
      setCommentError(null);
      try {
        await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/hmwBoard_comments`), {
          text: newComment.trim(),
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        });
        setNewComment('');
      } catch (err) {
        setCommentError('Failed to add comment.');
      }
      setCommentLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* HMW Submission Section */}
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 border border-orange-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
            <Lightbulb className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">How Might We Statements</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Submit and vote on problem statements</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <TextField
            label="How might we..."
            value={newHmw}
            onChange={e => setNewHmw(e.target.value)}
            size="small"
            fullWidth
            disabled={hmwLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#f97316'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#f97316',
                  borderWidth: '2px'
                }
              }
            }}
            placeholder="How might we solve this problem..."
            onKeyPress={(e) => e.key === 'Enter' && handleAddHmw()}
          />
          <Button
            variant="contained"
            onClick={handleAddHmw}
            disabled={hmwLoading || !newHmw.trim()}
            startIcon={<Add />}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #f97316 0%, #f59e0b 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #ea580c 0%, #d97706 100%) !important',
                color: '#ffffff !important'
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#6b7280'
              }
            }}
          >
            Submit
          </Button>
        </div>

        {hmwLoading && (
          <div className="flex items-center gap-2 text-orange-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Processing...</span>
          </div>
        )}
        
        {hmwError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {hmwError}
          </Alert>
        )}

        <div className="space-y-3">
          {hmws.map(hmw => (
            <div key={hmw.id} className="bg-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-gray-900 font-['Inter'] font-medium">{hmw.text}</p>
                  <p className="text-sm text-gray-500 font-['Inter'] mt-1">by {hmw.userName || 'Unknown'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={hmw.voters.includes(mockUserId) ? "contained" : "outlined"}
                    size="small"
                    startIcon={<ThumbUp />}
                    onClick={() => handleVote(hmw.id)}
                    disabled={hmw.voters.includes(mockUserId) || hmwLoading}
                    sx={{
                      borderRadius: '8px',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.75rem',
                      minWidth: 'auto',
                      px: 2,
                      color: hmw.voters.includes(mockUserId) ? '#ffffff' : '#f97316',
                      borderColor: '#f97316',
                      ...(hmw.voters.includes(mockUserId) && {
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: '#ffffff',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                          color: '#ffffff'
                        }
                      })
                    }}
                  >
                    {hmw.votes}
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteHmw(hmw.id)}
                    disabled={hmwLoading}
                    sx={{
                      color: '#ef4444',
                      '&:hover': { backgroundColor: '#fef2f2' }
                    }}
                  >
                    <Delete />
                  </IconButton>
                </div>
              </div>
            </div>
          ))}
        </div>

        {hmws.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Lightbulb className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-['Inter']">No "How Might We" statements yet. Be the first to submit one!</p>
          </div>
        )}
      </div>

      {/* Comments Section */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
            <Chat className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Team Discussion</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Share thoughts and collaborate</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <TextField
            label="Write a comment..."
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            size="small"
            fullWidth
            disabled={commentLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6',
                  borderWidth: '2px'
                }
              }
            }}
            placeholder="Share your thoughts..."
            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
          />
          <Button
            variant="contained"
            onClick={handleAddComment}
            disabled={commentLoading || !newComment.trim()}
            startIcon={<Send />}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%) !important',
                color: '#ffffff !important'
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#6b7280'
              }
            }}
          >
            Send
          </Button>
        </div>

        {commentLoading && (
          <div className="flex items-center gap-2 text-purple-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Sending comment...</span>
          </div>
        )}
        
        {commentError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {commentError}
          </Alert>
        )}

        <div className="space-y-3">
          {comments.map((comment, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex items-start gap-3">
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'purple.500' }}>
                  {(comment.userName || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <div className="flex-1">
                  <p className="text-gray-900 font-['Inter']">{comment.text}</p>
                  <p className="text-sm text-gray-500 font-['Inter'] mt-1">by {comment.userName || 'Unknown'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {comments.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Chat className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-['Inter']">No comments yet. Start the discussion!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HowMightWePhase; 